import { SessionActivityTracker } from "../session/SessionActivityTracker";
// The ECONIC frame (SPEC §5; DESIGN-SYSTEM §00 over §2; shell-ia §4–§13): the layout element of every signed-in route under
// `VITE_SHELL=econic`. From the top: the classification strip (22, only on Restricted and Confidential screens), the toolbar (48), the
// content column (#main on --bg-grouped, it scrolls itself), the agent inspector at the end side, the status bar (22); below 1024 px the
// bottom tab bar (and the phone dock below 768). The scene is WP-THEME's: the 268 px band at the top of Today, and nothing on any other
// screen (APPLE-SPEC §4.3). Nothing that contains #main or a desk frame has a transform, filter, backdrop-filter, will-change, contain or
// z-index (shell.css). WP-SHELL.
//
// Providers, outermost first (shell-ia §19.1): PreferencesProvider › DisplayEffects (theme, ♿, motion) › ClassificationProvider ›
// SidebarStateProvider › ConfirmProvider. The session layer (idle, absolute limit, arrival message) is WP-SEC-UI's SessionManager in
// app/providers.tsx, around every flag.
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { Toaster } from "sonner";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";
import { useVersionInfo } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { ConfirmProvider } from "@ncp/ui";
import { withLocales } from "../../app/locales-ready";
import { routeFor } from "../../app/routes";
import { useNewChat } from "../../app/useNewChat";
import { useWorkspace } from "../../app/workspace-context";
import { KeptPages } from "../../pages-host/KeptPages";
import { useShell } from "../../shell/state";
import { useDockAvailable } from "../../shell/dock";
import type { SceneExtent } from "../theme";
import { DisplayEffects, PreferencesProvider, useDisplaySettings } from "../prefs";
import { useAlignDepartmentWorkspace } from "../departments";
import { freshModuleConversation, isChatModule, moduleOfPath } from "../agent/moduleAgent";
import { LANDING_NEW_EVENT } from "../today/landing";
import { AgentColumn } from "./AgentColumn";
import { ClassificationBanner } from "./ClassificationBanner";
import { ClassificationProvider } from "./classification";
import { useShellKeyboard } from "./keyboard";
import { useStoredLanguage } from "./LanguageToggle";
import { PhoneDock } from "./MobileTabBar";
import { ShellPageHeader } from "./PageHeader";
import { AGENT_NEW_EVENT, SidebarStateProvider, useSidebarState } from "./sidebar-state";
import { StatusBar } from "./StatusBar";
import { TopBar } from "./TopBar";
import { GlobalDrop } from "./GlobalDrop";
import { RouteAnnouncer, ShellErrorBoundary, SkipLinks } from "./a11y";
import { useShellRealtime, useShellSummary } from "./useShellSummary";
// The start-a-study dialog brings the form engine (ajv, react-hook-form): it loads when a study is started.
const OpenMatterDialog = lazy(withLocales(() => import("../../features/matters/OpenMatterDialog").then((m) => ({ default: m.OpenMatterDialog }))));
// The scene photograph draws only behind Today's top band (APPLE-SPEC §1.4), so its catalogue loads with that screen, not with the frame.
const SceneBackdrop = lazy(() => import("../theme/SceneBackdrop").then((m) => ({ default: m.SceneBackdrop })));
// The department picker (⌘⇧D) loads the first time it is asked for, and stays mounted after that so it can animate closed.
const DepartmentDialog = lazy(withLocales(() => import("./DepartmentSwitcher").then((m) => ({ default: m.DepartmentDialog }))));
const HelpAbout = lazy(() => import("./HelpAbout").then((m) => ({ default: m.HelpAbout })));
// A page's About sheet (`?about=1`) loads when it is asked for.
const AppAbout = lazy(withLocales(() => import("../../os/AppAbout").then((m) => ({ default: m.AppAbout }))));
import { holdEconicShell, setDocumentAgentPage } from "../theme/document";
import { moduleByKey, moduleOfPath as agentOfPath } from "../modules";
import { AgentFaceProvider } from "../brand";
import "./shell.css";

/** Scene per screen (APPLE-SPEC §4.3): Today has the 268 px band at the top of the window; every other signed-in screen has no scene. */
function sceneExtentFor(pathname: string): SceneExtent {
  return pathname === "/" ? "top" : "none";
}

export function EconicShell({ palette, onPaletteChange }: { palette: boolean; onPaletteChange: (open: boolean) => void }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const def = routeFor(pathname);
  // ECONIC's tokens apply under html[data-shell="econic"] (tokens.css): set while the frame is mounted.
  useLayoutEffect(() => holdEconicShell(), []);
  return (
    <PreferencesProvider>
      <DisplayEffects>
        <ClassificationProvider floor={def?.classification ?? "internal"} floorSource={def ? t("assistant:econic.shell.class.floorOf", { screen: t(`assistant:${def.title}`) }) : ""}>
          <SidebarStateProvider>
            <ConfirmProvider>
              <AgentFaceProvider>
                <ShellFrame palette={palette} onPaletteChange={onPaletteChange} />
              </AgentFaceProvider>
            </ConfirmProvider>
          </SidebarStateProvider>
        </ClassificationProvider>
      </DisplayEffects>
    </PreferencesProvider>
  );
}

function ShellFrame({ palette, onPaletteChange }: { palette: boolean; onPaletteChange: (open: boolean) => void }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { dir } = useLocale();
  const def = routeFor(pathname);
  const layout = def?.layout === "bare" ? "page" : def?.layout ?? "page";
  const { scene } = useDisplaySettings();
  const { data: shell } = useShellSummary();
  const version = useVersionInfo();
  const sidebar = useSidebarState();
  const dockAvailable = useDockAvailable();
  const navigate = useNavigate();
  const { openPalette, openShortcuts } = useWorkspace();
  const { starting, stopStarting } = useShell();
  const newChat = useNewChat();
  const main = useRef<HTMLElement | null>(null);
  const [about, setAbout] = useState(false);
  const [departments, setDepartments] = useState(false);
  const [departmentsAsked, setDepartmentsAsked] = useState(false);
  useEffect(() => { if (departments) setDepartmentsAsked(true); }, [departments]);
  // The agent the screen belongs to (its page, or a conversation opened with it): its colour runs through the brand looks (looks/colour.css).
  const asked = params.get("agent");
  const agentPage = agentOfPath(pathname) ?? (pathname.startsWith("/agent") && asked && moduleByKey(asked) ? asked : null);
  useEffect(() => { setDocumentAgentPage(agentPage); }, [agentPage]);
  useEffect(() => () => setDocumentAgentPage(null), []);
  useShellRealtime();
  useStoredLanguage();
  useAlignDepartmentWorkspace();
  useShellKeyboard({
    openSearch: openPalette,
    openShortcuts,
    // ⌘J: a fresh conversation in the agent column (the column opens; WP-SIDEBAR's ChatSidebar starts it), else the full chat page; on an
    // agent's own page, a fresh conversation with that agent (its empty box); on Policy (PMO)'s, ECONIC's own (the ECONIC landing). On the
    // ECONIC landing, which is itself the ECONIC chat (no column), its box, emptied; another screen without the column opens the landing
    // (never a follow-me thread in a hidden column that nobody sees).
    newChat: () => {
      const agent = moduleOfPath(pathname);
      if (isChatModule(agent)) freshModuleConversation(agent, navigate);
      else if (agent) navigate("/");
      else if (pathname === "/") window.dispatchEvent(new Event(LANDING_NEW_EVENT));
      else if (sidebar.effective === "hidden" && !dockAvailable) newChat();
      else if (def?.sidebar === "none") navigate("/");
      else {
        sidebar.open();
        window.dispatchEvent(new Event(AGENT_NEW_EVENT));
      }
    },
    toggleAgent: sidebar.toggle,
    openDepartments: () => setDepartments(true),
  });

  // Each screen starts at its top (the content column scrolls itself).
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const environment = shell?.environment ?? (import.meta.env.DEV ? "Development" : null);
  const versionText = shell?.version ?? version.data?.version ?? null;

  return (
    <>
      <SessionActivityTracker />
      <SkipLinks />
      {sceneExtentFor(pathname) !== "none" ? <Suspense fallback={null}><SceneBackdrop scene={scene} extent={sceneExtentFor(pathname)} /></Suspense> : null}
      <ClassificationBanner />
      <TopBar search={{ open: palette, onOpenChange: onPaletteChange }} onOpenAbout={() => setAbout(true)} />
      <main id="main" ref={main} tabIndex={-1} className="es-main" data-layout={layout} data-route={def?.id} data-kept={def?.owner === "WP-SHELL" ? "" : undefined}>
        <div className="es-content">
          <ShellPageHeader />
          <KeptPages>
            <ShellErrorBoundary>
              <Outlet />
            </ShellErrorBoundary>
          </KeptPages>
        </div>
      </main>
      <AgentColumn />
      <StatusBar />
      <PhoneDock />
      <GlobalDrop />
      <RouteAnnouncer />
      {params.get("about") === "1" ? <Suspense fallback={null}><AppAbout /></Suspense> : null}
      {starting ? <Suspense fallback={null}><OpenMatterDialog open onOpenChange={(o) => !o && stopStarting()} playbookKey={starting.playbookKey} /></Suspense> : null}
      {about ? <Suspense fallback={null}><HelpAbout open={about} onOpenChange={setAbout} version={versionText} environment={environment} /></Suspense> : null}
      {departments || departmentsAsked ? <Suspense fallback={null}><DepartmentDialog open={departments} onOpenChange={setDepartments} /></Suspense> : null}
      <Toaster
        dir={dir}
        position="bottom-center"
        offset={{ bottom: "calc(var(--econic-chrome-bottom) + 14px)" }}
        mobileOffset={{ bottom: "calc(var(--econic-chrome-bottom) + 10px)" }}
        icons={{
          success: <CheckCircle2 className="size-4 text-[#7FD6E6]" aria-hidden />,
          error: <XCircle className="size-4 text-[#FFB4AC]" aria-hidden />,
          warning: <TriangleAlert className="size-4 text-[#F2C26B]" aria-hidden />,
          info: <Info className="size-4 text-[#7FD6E6]" aria-hidden />,
        }}
        toastOptions={{
          classNames: {
            toast: "es-toast !rounded-[10px] !border-0 !bg-[rgb(29_29_31/.9)] !text-white !shadow-[var(--shadow-pop)] !font-[inherit] !min-h-[40px] !py-2 !text-[13px]",
            title: "!text-[13px] !font-semibold !text-white",
            description: "!text-[12px] !text-white/80",
            actionButton: "!bg-transparent !text-[#9FD8FF] !rounded-[6px] !font-medium !text-[13px] hover:!bg-white/10",
            cancelButton: "!bg-white/10 !text-white !rounded-[6px]",
            error: "!bg-[rgb(120_20_20/.92)]",
          },
        }}
      />
    </>
  );
}
